import React, { useEffect } from 'react';

/**
 * Lightweight SEO helper.
 *
 * Supports:
 * - <title> and <meta name="description">
 * - <link rel="canonical">
 * - <meta name="robots"> (noindex toggle)
 * - Open Graph (og:title / og:description / og:type / og:image / og:url)
 * - Twitter Card (twitter:card / twitter:title / twitter:description / twitter:image)
 * - JSON-LD blocks (`jsonLd` and `faqJsonLd`) injected as <script type="application/ld+json"> tags.
 *
 * All injected nodes are tagged with `data-seohead="1"` so we can clean up
 * any per-page nodes on unmount or route change.
 */
export default function SeoHead({
  title,
  description,
  canonical,
  noindex = false,
  ogType = 'website',
  ogImage,
  twitterCard = 'summary_large_image',
  jsonLd,
  faqJsonLd,
}) {
  useEffect(() => {
    // Remove any per-page JSON-LD blocks injected by a previous render of SeoHead.
    document
      .querySelectorAll('script[data-seohead-jsonld="1"]')
      .forEach((n) => n.remove());

    if (title) document.title = title;

    const setMeta = (selector, attrName, attrValue, content) => {
      if (!content) return;
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('meta[name="description"]', 'name', 'description', description);

    // Open Graph
    setMeta('meta[property="og:title"]', 'property', 'og:title', title);
    setMeta('meta[property="og:description"]', 'property', 'og:description', description);
    setMeta('meta[property="og:type"]', 'property', 'og:type', ogType);
    if (canonical) setMeta('meta[property="og:url"]', 'property', 'og:url', canonical);
    if (ogImage) setMeta('meta[property="og:image"]', 'property', 'og:image', ogImage);

    // Twitter
    setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', twitterCard);
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    if (ogImage) setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', ogImage);

    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }

    // Robots meta. Set/remove based on noindex flag
    let robots = document.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', 'noindex, nofollow, noarchive, nosnippet');
    } else if (robots && robots.getAttribute('content')?.includes('noindex')) {
      robots.setAttribute('content', 'index, follow');
    }

    // JSON-LD injection (article + FAQ schemas)
    const injectJsonLd = (data) => {
      if (!data || typeof data !== 'object') return;
      try {
        const tag = document.createElement('script');
        tag.type = 'application/ld+json';
        tag.setAttribute('data-seohead-jsonld', '1');
        tag.textContent = JSON.stringify(data);
        document.head.appendChild(tag);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('[SeoHead] failed to inject JSON-LD', err);
        }
      }
    };
    injectJsonLd(jsonLd);
    injectJsonLd(faqJsonLd);

    return () => {
      // Clean up any JSON-LD injected by this render when the component unmounts
      // or before the next effect run.
      document
        .querySelectorAll('script[data-seohead-jsonld="1"]')
        .forEach((n) => n.remove());
    };
  }, [
    title,
    description,
    canonical,
    noindex,
    ogType,
    ogImage,
    twitterCard,
    jsonLd,
    faqJsonLd,
  ]);

  return null;
}
